define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-style",
    "dojo/Evented",
    "dojo/keys",
    "dojo/on",
    "dijit/layout/_LayoutWidget",
    "MW/layout/AccordionContainer",
    "MW/layout/BorderBar",
    "MW/layout/ChildLender",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/LayoutUtils",
    "MW/layout/PanelContainer",
    "MW/layout/Restorable",
    "MW/layout/TabContainer",
    "mw-log/Log",
    "dojo/i18n!l10n/nls/layout"
], function (declare, lang, dom, domAttr, domClass, domConstruct, domStyle, Evented, keys, on, _LayoutWidget,
             AccordionContainer, BorderBar, ChildLender, LayoutChildProperty, LayoutContainerEvent, LayoutUtils,
             PanelContainer, Restorable, TabContainer, Log, layoutL10n) {

    /**
     * Sub-class the AccordionContainer to temporarily lend out panels to the side bar
     */
    var LendingAccordionContainer = declare([AccordionContainer, ChildLender], {

        borrowChild: function (properties) {
            var panelInfo;
            var panelIndex = this._panelIndexFromProperties(properties);
            if (panelIndex >= 0) {
                panelInfo = this._panelInfo[panelIndex];
                this._superRemoveChild(panelInfo.widget);
                domStyle.set(panelInfo.widget.domNode, "display", "block");
                return panelInfo.widget;
            }
            return null;
        },

        returnChild: function (properties) {
            var panelInfo;
            var panelIndex = this._panelIndexFromProperties(properties);
            if (panelIndex >= 0) {
                panelInfo = this._panelInfo[panelIndex];
                if (panelInfo.isCollapsed) {
                    domStyle.set(panelInfo.widget.domNode, "display", "none");
                }
                this._superAddChild(panelInfo.widget);
            }
        },

        _superAddChild: function (child) {
            _LayoutWidget.prototype.addChild.apply(this, [child]);
        },

        _superRemoveChild: function (child) {
            _LayoutWidget.prototype.removeChild.apply(this, [child]);
        }
    });

    var DEFAULT_PANEL_SET_ID = "global";

    /**
     * A SideContainer is a collapsible container designed to display panels along the left or right edge of
     * a BorderContainer.  Its primary configuration consists of a TabContainer, holding one or more AccordionContainer
     * which in turn hold individual panels.  Panels are grouped into AccordionContainers based on a "setId"
     * property.
     *
     * The SideContainer's collapsed configuration is a BorderBar that displays a button per panel.
     */
    var SideContainer = declare([TabContainer, PanelContainer], {

        enableOnTopExpand: true,

        // region
        // panelSetTitles

        // _sideBar
        // _accordionContainers - hash from setId to AccordionContainer
        // _setInfo object keyed by setId that contains the following for each entry
        //     container
        //     containerState

        constructor: function () {
            this.isContentBorderVisible = false;
            this.tabPosition = "bottom";
            this.hideSingleTab = true;
            this.set(TabContainer.SHOWING_TAB_BACKGROUND, "rgb(245,245,245)");
            this.set(TabContainer.TAB_BACKGROUND, "rgb(230,230,230)");
            this._setInfo = {};
        },

        postMixInProperties: function () {
            var that = this;
            this.inherited(arguments);

            Log.assert(this.region, "No region specified for SideContainer");

            // Create the companion side bar
            if (!this.borderOptions || this.borderOptions.isCollapsible !== false) {
                this._sideBar = new BorderBar({
                    id: this.id + "SideBar",
                    region: this.region,
                    animationEnabled: this.animationEnabled
                });
                this._buttonBar = this._sideBar;
                if (this.enableOnTopExpand) {
                    this.collapsedWidget = this._sideBar;  // Alias by which the BorderContainer finds the collapsed widget
                }
                this._sideBar.on(LayoutContainerEvent.DRAG_OUT, function (eventData) {
                    eventData.widget = eventData.properties.widget;
                    that.emit(LayoutContainerEvent.DRAG_OUT, eventData);
                });
                this._sideBar.on(LayoutContainerEvent.STATE_CHANGED, function () {
                    that.emit(LayoutContainerEvent.STATE_CHANGED);
                });
            }
        },

        buildRendering: function () {
            this.inherited(arguments);
            domClass.add(this.domNode, "sideContainer");
            domClass.add(this.domNode, this.region + "SideContainer");
            if (!this.reserveSpaceForTabs) {
                domClass.add(this.domNode, "tabSpaceNotReserved");
            }

            // Add collapse button in tab area
            var that = this;
            if (!this.borderOptions || this.borderOptions.isCollapsible !== false) {
                if (this.reserveSpaceForTabs) {
                    this._collapseAllButton = domConstruct.create("div", {id: this.id + "CollapseAllButton", role: "button"}, this.domNode);
                    domClass.add(this._collapseAllButton, "collapseButton");
                    domAttr.set(this._collapseAllButton, "title", layoutL10n.collapseSidePanel);
                    dom.setSelectable(this._collapseAllButton, false);
                    LayoutUtils.preventFocus(this._collapseAllButton);
                    this._collapseAllButton.tabIndex = -1;
                    this._sideBar.collapseAllButton = this._collapseAllButton;
                    on(this._collapseAllButton, "click", function () {
                        that.emit(LayoutContainerEvent.COLLAPSE_ALL);
                    });

                    this.own(on(this._collapseAllButton, "keyup", lang.hitch(this, "_collapseAllButtonKeyUpHandler")));
                    this.own(on(this._collapseAllButton, "keydown", lang.hitch(this, "_collapseAllButtonKeyDownHandler")));

                    this.own(on(this._collapseAllButton, "focus", function () {
                        this.classList.add("collapseButtonFocused");
                    }));

                    this.own(on(this._collapseAllButton, "blur", function () {
                        this.classList.remove("collapseButtonFocused");
                    }));

                    if (this.region === "left") {
                        this._tabBar.setPreNeighbor(this._collapseAllButton);
                    } else {
                        this._tabBar.setPostNeighbor(this._collapseAllButton);
                    }
                }
                this._sideBar.on(LayoutContainerEvent.EXPAND_ALL, function () {
                    that.emit(LayoutContainerEvent.EXPAND_ALL);
                });
            }
        },

        addPanel: function (widget, properties) {
            var setInfo = this._getSetInfo(properties.setId);
            /*
            if (setInfo.id !== DEFAULT_PANEL_SET_ID) {
                properties.buttonSetId = setInfo.id;
            }
            */
            properties.widget = widget;
            if (!setInfo.container) {
                this._createAccordionContainer(setInfo);
                this._addAccordionContainer(setInfo);
                if (setInfo.pendingShowSetId) {
                    var showInfo = this._setInfo[setInfo.pendingShowSetId];
                    if (showInfo && showInfo !== setInfo && showInfo.container) {
                        this.showChild(showInfo.container);
                    }
                    delete setInfo.pendingShowSetId;
                }
            }
            if (properties) {
                setInfo.container.addWithProperties(widget, properties, properties.index);
            } else {
                setInfo.container.addChild(widget);
            }
            if (this._sideBar) {
                this._sideBar.acceptLoan(setInfo.container, properties || widget);
            }

            if (this.isContainerCollapsed()) {
                properties.set(LayoutChildProperty.IS_SHOWING, false);
            }
        },

        removePanel: function (widget, properties) {
            var setInfo = this._getSetInfo(properties.setId);
            var container = setInfo.container;
            if (container) {
                container.removeChild(widget);
            }
        },

        removeAll: function () {
            var setIds = Object.keys(this._setInfo);
            var index, setId, setInfo;
            for (index = 0; index < setIds.length; index++) {
                setId = setIds[index];
                setInfo = this._setInfo[setId];
                if (setInfo.container) {
                    setInfo.container.removeAll();
                }
                delete this._setInfo[setId];
            }
            if (this._sideBar) {
                this._sideBar.removeAll();
            }
        },

        getPanels: function () {
            var setIds = Object.keys(this._setInfo);
            var index, setId, setInfo;
            var panels = [];
            for (index = 0; index < setIds.length; index++) {
                setId = setIds[index];
                setInfo = this._setInfo[setId];
                if (setInfo.container) {
                    panels = panels.concat(setInfo.container.getPanels());
                }
            }
            return panels;
        },

        getPanelProperties: function () {
            var setIds = Object.keys(this._setInfo);
            var index, setId, setInfo;
            var properties = [];
            for (index = 0; index < setIds.length; index++) {
                setId = setIds[index];
                setInfo = this._setInfo[setId];
                if (setInfo.container) {
                    properties = properties.concat(setInfo.container.getPanelProperties());
                }
            }
            return properties;
        },

        getPanelCount: function () {
            var setIds = Object.keys(this._setInfo);
            var index, setId, setInfo;
            var count = 0;
            for (index = 0; index < setIds.length; index++) {
                setId = setIds[index];
                setInfo = this._setInfo[setId];
                if (setInfo.container) {
                    count += setInfo.container.getPanels().length;
                }
            }
            return count;
        },

        /**
         * Removes all of the panels belonging to the specified set and destroys the
         * accordion container they occupied.  Before doing all this the container state
         * is saved so that it can be restored if the panels belonging to the set are
         * subsequently added back.
         */
        removePanelSet: function (setId) {
            var setInfo = this._setInfo[setId];
            if (setInfo && setInfo.container) {
                setInfo.containerState = setInfo.container.getState();
                if (setInfo.container.getParent()) {
                    this.removeChild(setInfo.container);
                    setInfo.container.removeAll();
                    setInfo.container.destroyRecursive();
                    delete setInfo.container;
                }
            }
        },

        revealChild: function (widget) {
            /* Causes stack overflow
            var parent = widget.getParent();
            if (parent) {
                if (parent !== this.get(TabContainer.SHOWING_CHILD)) {
                    this.showChild(parent);
                }
                parent.revealChild(widget);
            }
            */
        },

        nextChild: function (widget, showing) {
            var next, containers, i;
            var showingContainer = this.get(TabContainer.SHOWING_CHILD);
            if (showingContainer) {
                next = showingContainer.nextChild(widget, showing);
                if (!next && !showing) {
                    containers = this.getChildrenInTabOrder();
                    if (containers.length > 1) {
                        i = containers.indexOf(showingContainer) + 1;
                        if (i >= containers.length) {
                            i = 0;
                        }
                        next = containers[i].nextChild(null, showing);
                    }
                }
            }
            return next;
        },

        previousChild: function (widget, showing) {
            var prev, containers, i;
            var showingContainer = this.get(TabContainer.SHOWING_CHILD);
            if (showingContainer) {
                prev = showingContainer.previousChild(widget, showing);
                if (!prev && !showing) {
                    containers = this.getChildrenInTabOrder();
                    if (containers.length > 1) {
                        i = containers.indexOf(showingContainer) - 1;
                        if (i < 0) {
                            i = containers.length - 1;
                        }
                        prev = containers[i].previousChild(null, showing);
                    }
                }
            }
            return prev;
        },

        /**
         * Gets the state of the specified panel set (the default set if none is specified)
         */
        getState: function (setId) {
            setId = setId || DEFAULT_PANEL_SET_ID;
            var setInfo = this._setInfo[setId];
            var state = {};
            if (setInfo) {
                if (setInfo.container) {
                    state.children = setInfo.container.getState();
                    var showingContainer = this.get(TabContainer.SHOWING_CHILD);
                    if (showingContainer) {
                        state.showingSetId = showingContainer.setInfo.id;
                    }
                } else if (setInfo.containerState) {
                    state.children = setInfo.containerState;
                }
            }
            return state;
        },

        /**
         * Sets the state of either the entire container or a specific panel set
         */
        setState: function (state, setId) {
            setId = setId || DEFAULT_PANEL_SET_ID;
            var setInfo = this._getSetInfo(setId);
            var stateChildren = state.children || state.panels;
            if (stateChildren) {
                if (setInfo.container) {
                    setInfo.container.setState(stateChildren);
                } else {
                    setInfo.containerState = stateChildren;
                    setInfo.pendingShowSetId = state.showingSetId;
                }
            }
        },

        /**
         * Returns true if the container state is in the process of being restored
         */
        isRestoringState: function () {
            var defaultInfo = this._setInfo[DEFAULT_PANEL_SET_ID];
            return !!(defaultInfo && (defaultInfo.containerState ||
                                     (defaultInfo.container && defaultInfo.container.isRestoringState())));
        },

        /**
         * Terminates the state restoration process if it is in progress
         */
        endStateRestore: function() {
            var setIds = Object.keys(this._setInfo);
            var index, setId, setInfo;
            for (index = 0; index < setIds.length; index++) {
                setId = setIds[index];
                setInfo = this._setInfo[setId];
                if (setInfo.container && setInfo.container.isRestoringState()) {
                    setInfo.container.endStateRestore();
                }
            }
        },

        isExpecting: function (panelId) {
            var setIds = Object.keys(this._setInfo);
            var index, setId, setInfo;
            for (index = 0; index < setIds.length; index++) {
                setId = setIds[index];
                setInfo = this._getSetInfo(setId);
                if ((setInfo.container && setInfo.container.isExpecting(panelId)) ||
                    (setInfo.containerState && AccordionContainer.stateIncludes(setInfo.containerState, panelId))){
                    return true;
                }
            }
            return false;
        },

        _getSetInfo: function (setId) {
            setId = setId || DEFAULT_PANEL_SET_ID;
            var setInfo = this._setInfo[setId];
            if (!setInfo) {
                setInfo = {id: setId};
                this._setInfo[setId] = setInfo;
            }
            return setInfo;
        },

        _createAccordionContainer: function (setInfo) {
            var that = this;
            var container, title;
            if (this.panelSetTitles) {
                title = this.panelSetTitles[setInfo.id];
            }
            if (!title) {
                title = setInfo.id;
            }
            container = new LendingAccordionContainer({
                id: this.id + (setInfo.id === DEFAULT_PANEL_SET_ID ? "" : setInfo.id) + "AccordionContainer",
                region: this.region,
                title: title,
                setInfo: setInfo
            });
            // Respond to container event
            container.on(LayoutContainerEvent.DRAG_OUT, function (eventData) {
                that.emit(LayoutContainerEvent.DRAG_OUT, eventData);
            });
            container.on(LayoutContainerEvent.CHILD_ADDED, function (widget, properties) {
                Evented.prototype.emit.apply(that, [LayoutContainerEvent.CHILD_ADDED, widget, properties]);
            });
            container.on(LayoutContainerEvent.CHILD_REMOVED, function (widget, properties) {
                if (that._sideBar) {
                    that._sideBar.cancelLoan(properties);
                }
                if (container.isEmpty()) {
                    that.removeChild(container, true);
                    container.destroyRecursive();
                    delete setInfo.container;
                }
                Evented.prototype.emit.apply(that, [LayoutContainerEvent.CHILD_REMOVED, widget, properties]);
            });
            container.on(LayoutContainerEvent.STATE_CHANGED, function (eventData) {
                that.emit(LayoutContainerEvent.STATE_CHANGED, eventData);
            });
            container.watch(LayoutChildProperty.IS_SHOWING, function (name, oldValue, newValue) {
                that._updateChildren(container, newValue);
            });
            // Restore state if pending
            if (setInfo.containerState) {
                container.setState(setInfo.containerState);
                delete setInfo.containerState;
            }
            if (this._collapseAllButton) {
                container.addExtraNavigationTarget(this._collapseAllButton);
            }
            setInfo.container = container;
        },

        _addAccordionContainer: function (setInfo) {
            // The default set should always be first
            if (setInfo.id === DEFAULT_PANEL_SET_ID) {
                this.addChild(setInfo.container, 0, true, true);
            } else {
                this.addChild(setInfo.container, undefined, true, true);
            }
        },

        collapseChange: function (isCollapsed) {
            this.inherited(arguments);
            var container = this.get(TabContainer.SHOWING_CHILD);
            if (container) {
                this._updateChildren(container, !isCollapsed);
            }
        },

        _updateChildren: function (container, showing) {
            // Update the showing property of non-collapsed panels in the given container
            container.getPanelProperties().forEach(function (properties) {
                if (!properties.get(LayoutChildProperty.IS_COLLAPSED)) {
                    properties.set(LayoutChildProperty.IS_SHOWING, showing);
                }
            });
        },

        dragIn: function (dragOutInfo, mousePoint) {
            // Make sure there is a home for the panel, i.e. an accordion container for
            // the set it belongs
            var properties = dragOutInfo.properties;
            var setInfo = this._getSetInfo(properties.setId);
            /*
            if (setInfo.id !== DEFAULT_PANEL_SET_ID) {
                properties.buttonSetId = setInfo.id;
            }
            */
            if (!setInfo.container) {
                this._createAccordionContainer(setInfo);
                this._addAccordionContainer(setInfo);
            }

            // Drag the panel into whichever is showing, the accordion container or the side bar.
            if (this.isOrWasContainerCollapsed(dragOutInfo)) {
                // Add to accordion container and drag into side bar
                setInfo.container.addWithProperties(dragOutInfo.widget, properties);
                if (this._sideBar) {
                    this._sideBar.dragChildIn(setInfo.container, dragOutInfo, mousePoint);
                }
                this.reCollapseContainer(dragOutInfo);
            } else {
                // Make sure accordion container is showing then drag into it
                // and add to side bar
                this.showChild(setInfo.container);
                setInfo.container.dragIn(dragOutInfo, mousePoint);
                if (this._sideBar) {
                    this._sideBar.acceptLoan(setInfo.container, properties);
                }
            }

            if (this.isContainerCollapsed()) {
                dragOutInfo.properties.set(LayoutChildProperty.IS_SHOWING, false);
            }
        },

        /**
         * Bypass the on function override inherited from TabContainer since it will redirect DRAG_OUT event
         * listening to the TabBar.
         */
        on: function(type, func){
            return Evented.prototype.on.apply(this, [type, func]);
        },

        /**
         * Suppress tab container child added and removed events.  The side container parent is presumably only
         * interested in panel add and remove.
         */
        emit: function(type, event) {
            if (type !== LayoutContainerEvent.CHILD_ADDED && type !== LayoutContainerEvent.CHILD_REMOVED) {
                this.inherited(arguments);
            }
        },

        _collapseAllButtonKeyUpHandler: function (e) {
            if (e.keyCode === keys.SPACE) {
                e.preventDefault();
                this._collapseSideContainer();
            }
        },

        _collapseAllButtonKeyDownHandler: function (e) {
            if (e.keyCode === keys.ENTER) {
                this._collapseSideContainer();
            }
        },

        _collapseSideContainer: function () {
            var parent, selectionManager, that = this;

            parent = this;
            while (!(parent && parent.selectionManager)) {
                parent = parent.getParent();
            }
            selectionManager = parent.selectionManager;

            selectionManager.blockSuccession();
            that.emit(LayoutContainerEvent.COLLAPSE_ALL);
            setTimeout(function() {
                selectionManager.unblockSuccession();
                if (that.collapsedWidget.getPostNeighbor) {
                    that.collapsedWidget.getPostNeighbor().tabIndex = -1;
                    that.collapsedWidget.getPostNeighbor().focus();
                }
            }, 400);
        }
    });

    SideContainer.DEFAULT_PANEL_SET_ID = DEFAULT_PANEL_SET_ID;

    /**
     * Static helper function that returns true if the given container state object includes the specified panel ID
     */
    SideContainer.stateIncludes = function (state, panelId) {
        var stateChildren = state.children || state.panels;
        return stateChildren && AccordionContainer.stateIncludes(stateChildren, panelId);
    };

    return SideContainer;
});
